import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import {svg} from '../assets/svg';
import {theme} from '../constants';

type Props = {
  user?: boolean;
  logo?: boolean;
  title?: string;
  line?: boolean;
  search?: boolean;
  burger?: boolean;
  goBack?: boolean;
  basket?: boolean;
  creditCard?: boolean;
  documentIcon?: boolean;
};

export const Header: React.FC<Props> = ({
  logo,
  line,
  user,
  title,
  goBack,
  creditCard,
  documentIcon,
}) => {
  const navigate = useNavigate();
  const {t} = useTranslation();

  // Read hotel name from localStorage (set after bind / profile refresh).
  const hotelName = localStorage.getItem('guest_hotel_name');

  const renderGoBack = (): JSX.Element | null => {
    const canGoBack = window.history.length > 1;
    if (goBack && canGoBack) {
      return (
        <button
          type='button'
          style={{position: 'absolute', insetInlineStart: 0, padding: '0 18px', height: '100%', display: 'flex', alignItems: 'center'}}
          onClick={() => navigate(-1)}
          aria-label={t('common.back')}
        >
          <svg.GoBackSvg />
        </button>
      );
    }
    return null;
  };

  const renderUser = (): JSX.Element | null => {
    if (!user) return null;
    return (
      <div
        style={{
          position: 'absolute',
          insetInlineStart: 0,
          padding: '0 18px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => navigate('/Profile')}
      >
        <img
          alt='avatar'
          src='https://george-fx.github.io/apitex/users/01.png'
          style={{width: 28, height: 28, borderRadius: 14, objectFit: 'cover'}}
        />
      </div>
    );
  };

  const renderLogo = (): JSX.Element | null => {
    if (!logo) return null;
    return (
      <div
        style={{
          position: 'absolute',
          insetInlineStart: 0,
          padding: '0 18px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <img
          src='/logo.webp'
          alt='Hotel logo'
          style={{height: 30, width: 'auto', objectFit: 'contain', flexShrink: 0}}
        />
        {hotelName && (
          <span style={{
            fontSize: 14,
            fontWeight: 700,
            color: theme.colors.mainDark,
            fontFamily: "'SS3', sans-serif",
            letterSpacing: '-0.01em',
            maxWidth: 160,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {hotelName}
          </span>
        )}
      </div>
    );
  };

  const renderTitle = (): JSX.Element => (
    <span
      style={{
        left: '50%',
        textAlign: 'center',
        position: 'absolute',
        width: 'calc(100% - 110px)',
        transform: 'translateX(-50%)',
        color: theme.colors.mainDark,
        fontWeight: 600,
        fontSize: 16,
        fontFamily: "'SS3', sans-serif",
        letterSpacing: '-0.01em',
      }}
    >
      {title}
    </span>
  );

  const renderCreditCard = (): JSX.Element | null => {
    if (!creditCard) return null;
    return (
      <button
        type='button'
        style={{
          position: 'absolute',
          insetInlineEnd: 0,
          padding: '0 18px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: 'transparent',
          border: 'none',
        }}
        onClick={() => navigate('/CardMenu')}
      >
        <svg.CreditCardSvg />
      </button>
    );
  };

  const renderDocument = (): JSX.Element | null => {
    if (!documentIcon) return null;
    return (
      <button
        type='button'
        style={{
          position: 'absolute',
          insetInlineEnd: 0,
          padding: '0 18px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: 'transparent',
          border: 'none',
        }}
      >
        <svg.DocumentSvg />
      </button>
    );
  };

  const headerStyle: React.CSSProperties = {
    height: 56,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    top: 0,
    position: 'fixed',
    maxWidth: 650,
    zIndex: 50,
    backgroundColor: theme.colors.white,
    borderBottom: line ? `1px solid ${theme.colors.aliceBlue2}` : 'none',
  };

  return (
    <header style={headerStyle}>
      {renderGoBack()}
      {renderUser()}
      {renderLogo()}
      {renderTitle()}
      {renderDocument()}
      {renderCreditCard()}
    </header>
  );
};
